/**
 * Structured-data (schema.org JSON-LD) for a KidHive class listing.
 *
 * A kids' class is better modeled than a generic Product:
 *   - TERM-ENROLLMENT classes have a concrete dated session list
 *     (publicData.termSchedule.sessions[] as ISO instants), so they emit an EducationEvent with a
 *     real startDate/endDate + location + offer -> eligible for Google Event rich results.
 *   - ONE-TIME drop-ins (publicData.oneTimeSchedule.sessions[]) run on a real date too -> the same
 *     EducationEvent.
 *   - LEGACY weekly drop-ins are a recurring offering with no single fixed date, so they emit a
 *     Course (provider + offer) -- valid without a date.
 *   - Anything missing the data falls back to Product, so we never emit invalid Event markup
 *     (Google ignores/penalizes invalid structured data).
 *
 * Pure and unit-tested. `priceForSchema` is injected (the caller passes ListingPage.shared's
 * priceForSchemaMaybe) so this module stays free of Money/SDK imports.
 */

const isNonEmptyString = v => typeof v === 'string' && v.trim() !== '';
const isPlainObject = v => v != null && typeof v === 'object' && !Array.isArray(v);

const SINGLE_SESSION = 'single-session';
const TERM_ENROLLMENT = 'term-enrollment';

/** First/last dated session of a term class from publicData.termSchedule (ISO strings) or null. */
export const termSessionBounds = (publicData, scheduleKey = 'termSchedule') => {
  const schedule = isPlainObject(publicData) ? publicData[scheduleKey] : null;
  const sessions = schedule && Array.isArray(schedule.sessions) ? schedule.sessions : [];
  const valid = sessions.filter(s => isPlainObject(s) && isNonEmptyString(s.start));
  if (valid.length === 0) {
    return null;
  }
  const starts = valid.map(s => s.start).sort();
  const ends = valid.map(s => s.end).filter(isNonEmptyString).sort();
  return { startDate: starts[0], endDate: ends.length ? ends[ends.length - 1] : null };
};

const placeMaybe = publicData => {
  const address =
    isPlainObject(publicData) && isPlainObject(publicData.location)
      ? publicData.location.address
      : null;
  return isNonEmptyString(address)
    ? { location: { '@type': 'Place', name: address, address } }
    : {};
};

/**
 * Build the class JSON-LD.
 * @param {Object} opts
 * @param {Object} opts.listing a (denormalized) listing entity
 * @param {string} opts.schemaTitle
 * @param {string} opts.description
 * @param {string[]} opts.images absolute image URLs
 * @param {Object} opts.price a Money-shaped price (passed straight to priceForSchema)
 * @param {string} opts.productURL absolute listing URL
 * @param {string} opts.providerName
 * @param {Object} [opts.availabilityMaybe] { availability } for the offer
 * @param {Function} opts.priceForSchema price -> { price, priceCurrency } (or {})
 * @returns {Object} JSON-LD
 */
export const buildClassSchema = ({
  listing,
  schemaTitle,
  description,
  images,
  price,
  productURL,
  providerName,
  availabilityMaybe = {},
  priceForSchema,
}) => {
  const publicData = (listing && listing.attributes && listing.attributes.publicData) || {};
  const classFormat = publicData.classFormat;

  const offers = {
    '@type': 'Offer',
    url: productURL,
    ...(typeof priceForSchema === 'function' ? priceForSchema(price) : {}),
    ...availabilityMaybe,
  };
  const organizer = isNonEmptyString(providerName)
    ? { '@type': 'Organization', name: providerName }
    : {};

  // TERM with real dated sessions -> EducationEvent.
  // ONE-TIME drop-in with its dated session(s) -> the same EducationEvent.
  const bounds =
    classFormat === TERM_ENROLLMENT
      ? termSessionBounds(publicData)
      : classFormat === SINGLE_SESSION && publicData.scheduleInput?.oneTime === true
      ? termSessionBounds(publicData, 'oneTimeSchedule')
      : null;
  if (bounds) {
    return {
      '@context': 'https://schema.org',
      '@type': 'EducationEvent',
      name: schemaTitle,
      description,
      image: images,
      startDate: bounds.startDate,
      ...(bounds.endDate ? { endDate: bounds.endDate } : {}),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      ...placeMaybe(publicData),
      ...(isNonEmptyString(providerName) ? { organizer } : {}),
      offers,
    };
  }

  // SINGLE-SESSION (recurring) or a term without dated sessions -> Course (valid without a date).
  if (classFormat === SINGLE_SESSION || classFormat === TERM_ENROLLMENT) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: schemaTitle,
      description,
      image: images,
      ...(isNonEmptyString(providerName)
        ? { provider: { '@type': 'Organization', name: providerName } }
        : {}),
      offers,
    };
  }

  // Not a KidHive class (no classFormat) -> the stock Product schema.
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: schemaTitle,
    description,
    image: images,
    offers,
  };
};
